import noop from "lib/noop"
import { Box } from "@mui/material"
import { EDITABLE_STYLE } from "lib/@components/editable/style"
import { useCallback, useEffect, useRef } from "react"
import { prevent } from "lib/prevent"
import { useEditing } from "lib/@components/editable/useEditing"
import { Editing } from "lib/@components/editable/editing"
import { EditField } from "event-definitions"
import { getCurrentCursorPosition } from "lib/@components/editable/getCurrentCursorPosition"
import { createRange } from "lib/@components/editable/createRange"
import { setCurrentCursorPosition } from "lib/@components/editable/setCurrentCursorPosition"
import { EditableBox } from "lib/@components/editable/editableBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { bind } from "lib/@components/binding/bind"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"

export const BoundEditableText = bind(<EditableText />, { extract: (v) => v, omit: ["helperText", "error"] })
export const BoundEditableBox = bind(<EditableBox />, { extract: (v) => v, omit: ["helperText", "error"] })

export function EditableText({
    field,
    value,
    readOnly,
    preserveHtml,
    onChange = noop,
    pt = 0.8,
    pb = 0.8,
    pl = 1.5,
    pr = 1.5,
    sx,
    outerSx,
    ...props
}) {
    const { target } = useBoundContext()
    const cursor = useRef(0)
    const ref = useRef()
    const isFocused = useRef(false)
    EditField.useEvent(({ target: desiredTarget, field: desiredField }) => {
        if (desiredField === field && desiredTarget === target) {
            ref.current?.focus()
            const range = createRange(ref.current, { count: value.length })
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
        }
    })
    useEffect(() => {
        try {
            if (document.activeElement === ref.current) {
                if (cursor.current) {
                    setCurrentCursorPosition(ref.current, cursor.current)
                }
            }
        } catch (e) {
            //
        }
    }, [value])
    const setInnerHtml = useCallback(setInnerHtmlFromValue, [value, preserveHtml])
    const [, edit] = useEditing()

    const displaySx = { wordBreak: "break-word", ...sx }
    const innerSx = { pt, pb, pl, pr, ...displaySx }

    return (
        <If value={!!readOnly}>
            <Box display="inline-block" sx={outerSx}>
                <Box
                    {...props}
                    minHeight={10}
                    sx={{ ...EDITABLE_STYLE, ...innerSx }}
                    dangerouslySetInnerHTML={{ __html: value ?? "" }}
                />
            </Box>
            <Else>
                <Editing
                    field={field}
                    editor={
                        <Box
                            display="inline-block"
                            onKeyDown={handleSpaceKey}
                            onClick={prevent(edit)}
                            onFocus={edit}
                            sx={outerSx}
                        >
                            <Box
                                {...props}
                                ref={setInnerHtml}
                                tabIndex={0}
                                contentEditable
                                onInput={handleInput}
                                onPaste={preserveHtml ? handlePaste : undefined}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                minHeight={10}
                                sx={{ ...EDITABLE_STYLE, ...innerSx }}
                            />
                        </Box>
                    }
                    display={
                        <Box
                            display="inline-block"
                            sx={displaySx}
                            minHeight={10}
                            dangerouslySetInnerHTML={{ __html: value ?? "" }}
                        />
                    }
                />
            </Else>
        </If>
    )

    function setInnerHtmlFromValue(element) {
        ref.current = element
        if (!element) return
        if (preserveHtml && isFocused.current) return
        if (element.innerHTML !== (value ?? "")) {
            element.innerHTML = value ?? ""
        }
    }

    function handleFocus() {
        isFocused.current = true
    }

    function handleBlur() {
        isFocused.current = false
    }

    function handleSpaceKey(event) {
        if (event.code !== "Space") {
            return
        }
        event.preventDefault()
        document.execCommand("insertText", false, " ")
    }

    function handlePaste(event) {
        // In preserveHtml mode, only allow plain text paste to prevent
        // rich formatting (bold, italic, divs) from being stored as HTML.
        // Macro chips are already in the DOM and unaffected by paste.
        event.preventDefault()
        const text = event.clipboardData.getData("text/plain")
        document.execCommand("insertText", false, text)
    }

    function handleInput(event) {
        if (preserveHtml) {
            onChange(ref.current.innerHTML)
            return
        }
        cursor.current = getCurrentCursorPosition(ref.current.id)
        onChange(event.target.innerText)
    }
}
